import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
const CertificationsDetail: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Images de certifications (à remplacer par les vraies images)
  const certificationImages = [{
    id: 1,
    title: "Google AI Essentials",
    issuer: "Google",
    image: "/lovable-uploads/31aac4a4-a84d-4e5a-99c5-a403c80ecbd0.png",
    description: "Certification Google sur les fondamentaux de l'intelligence artificielle"
  }, {
    id: 2,
    title: "Microsoft Cybersecurity",
    issuer: "Microsoft",
    image: "/lovable-uploads/efea5584-fcc4-4dd8-9ddd-d015143d1b77.png",
    description: "Certification Microsoft en cybersécurité"
  }, {
    id: 3,
    title: "Apple Search Ads",
    issuer: "Apple",
    image: "/lovable-uploads/f5c547e4-1b70-4571-807f-fae5419e8a0c.png",
    description: "Certification Apple pour la publicité sur l'App Store"
  }, {
    id: 4,
    title: "LinkedIn Learning",
    issuer: "LinkedIn",
    image: "/lovable-uploads/adf0547f-2cee-4c69-970b-bb8a1c7664af.png",
    description: "Certifications LinkedIn Learning en management et développement"
  }, {
    id: 5,
    title: "Siemens Project Management",
    issuer: "Siemens",
    image: "/lovable-uploads/ac0b53ac-bd3c-4dc8-a6a3-2d0e1a1ed9ab.png",
    description: "Formation Siemens en gestion de projet"
  }, {
    id: 6,
    title: "ANSSI Cybersécurité",
    issuer: "ANSSI",
    image: "/lovable-uploads/36494d6c-anssi-logo.png",
    description: "Formation cybersécurité par l'ANSSI"
  }];
  return <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-purple-950/10">
      {/* Header avec navigation */}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-muted transition-colors text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            Retour aux certifications
          </button>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-500 to-cyan-400 bg-clip-text text-transparent">Quelques certifications dont je suis fier !</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Découvrez quelques-unes de mes certifications les plus importantes avec des effets visuels immersifs
          </p>
        </motion.div>

        {/* Carrousel de certifications */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative">
          <Carousel className="w-full max-w-4xl mx-auto">
            <CarouselContent>
              {certificationImages.map(cert => <CarouselItem key={cert.id}>
                  <div className="p-6">
                    <motion.div className="relative group" whileHover={{
                  scale: 1.02
                }} transition={{
                  duration: 0.3
                }}>
                      {/* Effet de projecteur */}
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-transparent to-cyan-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 -z-10"></div>
                      
                      {/* Carte de certification */}
                      <div className="relative bg-background/90 backdrop-blur-md rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                        <div className="relative h-96 overflow-hidden">
                          {/* Image de la certification */}
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 to-cyan-900/50"></div>
                          <img src={cert.image} alt={cert.title} className="w-full h-full object-contain p-8 filter brightness-110 contrast-110" />
                          
                          {/* Overlay avec effet de lumière */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          
                          {/* Effet de brillance */}
                          <div className="absolute top-0 left-0 w-full h-full opacity-0 group-hover:opacity-30 transition-opacity duration-700">
                            <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white rounded-full blur-3xl animate-pulse"></div>
                            <div className="absolute bottom-1/3 right-1/3 w-24 h-24 bg-purple-400 rounded-full blur-2xl animate-pulse delay-300"></div>
                          </div>
                        </div>
                        
                        {/* Informations de la certification */}
                        <div className="p-8 text-center">
                          <h3 className="text-2xl font-bold mb-2 text-white">
                            {cert.title}
                          </h3>
                          <p className="text-purple-300 font-medium mb-4">
                            {cert.issuer}
                          </p>
                          <p className="text-muted-foreground leading-relaxed">
                            {cert.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <CarouselPrevious className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-background/80 border-white/20 hover:bg-background/90" />
            <CarouselNext className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-background/80 border-white/20 hover:bg-background/90" />
          </Carousel>
        </motion.div>

        {/* Instructions de navigation */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="text-center mt-12">
          <p className="text-muted-foreground flex items-center justify-center gap-4">
            <span className="flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              {t('navigation.use_arrows')}
              <ChevronRight className="w-4 h-4" />
            </span>
          </p>
        </motion.div>

        {/* Retour vers la section certifications */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 1
      }} className="text-center mt-16">
          <motion.button onClick={() => navigate('/#certifications')} className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300" whileHover={{
          scale: 1.05,
          boxShadow: "0 20px 40px rgba(168,85,247,0.3)"
        }} whileTap={{
          scale: 0.95
        }}>
            {t('navigation.back_to_certifications')}
          </motion.button>
        </motion.div>
      </div>
    </div>;
};
export default CertificationsDetail;